import * as React from "react";
import {
  Box,
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ExpandMore,
  Logout,
  Menu as MenuIcon,
} from "@mui/icons-material";
import {
  isVisible,
  managementGroup,
  MenuGroup,
  MenuLeaf,
  reportsGroup,
  shipperBeneficiaryItems,
  topLevelItems,
} from "./menuConfig";

interface Props {
  isAdmin: boolean;
  isAdminOrManager: boolean;
  isShipperBeneficiary: boolean;
  onNavigate: (path: string) => void;
  onLogout: () => void;
  currentPath: string;
}

const activeSx = { backgroundColor: "rgba(255, 255, 255, 0.16)" };

export default function HorizontalMenu(props: Props) {
  const {
    isAdmin,
    isAdminOrManager,
    isShipperBeneficiary,
    onNavigate,
    onLogout,
    currentPath,
  } = props;

  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("md"));

  const [reportsAnchor, setReportsAnchor] = React.useState<null | HTMLElement>(
    null
  );
  const [mgmtAnchor, setMgmtAnchor] = React.useState<null | HTMLElement>(null);
  const [burgerAnchor, setBurgerAnchor] = React.useState<null | HTMLElement>(
    null
  );

  const visibleChildren = (group: MenuGroup) =>
    group.children.filter((leaf) =>
      isVisible(leaf.role, isAdmin, isAdminOrManager)
    );

  const groupActive = (group: MenuGroup) =>
    visibleChildren(group).some((leaf) => leaf.path === currentPath);

  const handleNavigate = (path: string) => {
    onNavigate(path);
    setReportsAnchor(null);
    setMgmtAnchor(null);
    setBurgerAnchor(null);
  };

  const renderTopButton = (leaf: MenuLeaf) => {
    const Icon = leaf.icon;
    return (
      <Button
        key={leaf.path}
        color="inherit"
        startIcon={<Icon />}
        onClick={() => handleNavigate(leaf.path)}
        sx={{
          whiteSpace: "nowrap",
          flexShrink: 0,
          ...(leaf.path === currentPath ? activeSx : {}),
        }}
      >
        {leaf.label}
      </Button>
    );
  };

  const renderDropdown = (
    group: MenuGroup,
    anchor: null | HTMLElement,
    setAnchor: (el: null | HTMLElement) => void
  ) => {
    const children = visibleChildren(group);
    if (children.length === 0) {
      return null;
    }
    const GroupIcon = group.icon;
    return (
      <>
        <Button
          color="inherit"
          startIcon={<GroupIcon />}
          endIcon={<ExpandMore />}
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{
            whiteSpace: "nowrap",
            flexShrink: 0,
            ...(groupActive(group) ? activeSx : {}),
          }}
        >
          {group.label}
        </Button>
        <Menu
          anchorEl={anchor}
          open={Boolean(anchor)}
          onClose={() => setAnchor(null)}
        >
          {children.map((leaf) => {
            const Icon = leaf.icon;
            return (
              <MenuItem
                key={leaf.path}
                selected={leaf.path === currentPath}
                onClick={() => handleNavigate(leaf.path)}
              >
                <ListItemIcon>
                  <Icon />
                </ListItemIcon>
                <ListItemText>{leaf.label}</ListItemText>
              </MenuItem>
            );
          })}
        </Menu>
      </>
    );
  };

  // Narrow screens: collapse the whole tree into a single hamburger menu.
  if (isNarrow) {
    const groups = isShipperBeneficiary
      ? []
      : [reportsGroup, managementGroup];
    const topItems = isShipperBeneficiary
      ? shipperBeneficiaryItems
      : topLevelItems;

    return (
      <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
        <IconButton
          color="inherit"
          edge="start"
          aria-label="meniu"
          onClick={(e) => setBurgerAnchor(e.currentTarget)}
        >
          <MenuIcon />
        </IconButton>
        <Menu
          anchorEl={burgerAnchor}
          open={Boolean(burgerAnchor)}
          onClose={() => setBurgerAnchor(null)}
        >
          {topItems.map((leaf) => {
            const Icon = leaf.icon;
            return (
              <MenuItem
                key={leaf.path}
                selected={leaf.path === currentPath}
                onClick={() => handleNavigate(leaf.path)}
              >
                <ListItemIcon>
                  <Icon />
                </ListItemIcon>
                <ListItemText>{leaf.label}</ListItemText>
              </MenuItem>
            );
          })}
          {groups.map((group) => {
            const children = visibleChildren(group);
            if (children.length === 0) {
              return null;
            }
            return [
              <ListSubheader key={`${group.label}-header`} disableSticky>
                {group.label}
              </ListSubheader>,
              ...children.map((leaf) => {
                const Icon = leaf.icon;
                return (
                  <MenuItem
                    key={leaf.path}
                    selected={leaf.path === currentPath}
                    onClick={() => handleNavigate(leaf.path)}
                    sx={{ pl: 4 }}
                  >
                    <ListItemIcon>
                      <Icon />
                    </ListItemIcon>
                    <ListItemText>{leaf.label}</ListItemText>
                  </MenuItem>
                );
              }),
            ];
          })}
          <MenuItem
            onClick={() => {
              setBurgerAnchor(null);
              onLogout();
            }}
          >
            <ListItemIcon>
              <Logout />
            </ListItemIcon>
            <ListItemText>Deconectare</ListItemText>
          </MenuItem>
        </Menu>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        overflowX: "auto",
        flexGrow: 1,
      }}
    >
      {isShipperBeneficiary ? (
        <>
          {shipperBeneficiaryItems.map(renderTopButton)}
          <Button
            color="inherit"
            startIcon={<Logout />}
            onClick={onLogout}
            sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
          >
            Deconectare
          </Button>
        </>
      ) : (
        <>
          {topLevelItems.map(renderTopButton)}
          {renderDropdown(reportsGroup, reportsAnchor, setReportsAnchor)}
          {renderDropdown(managementGroup, mgmtAnchor, setMgmtAnchor)}
          <Button
            color="inherit"
            startIcon={<Logout />}
            onClick={onLogout}
            sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
          >
            Deconectare
          </Button>
        </>
      )}
    </Box>
  );
}
